import React, { useEffect } from 'react';
import { Field, Loader } from 'ui';
import ListFlowDetailSelector from '~/components/ListFlow/InfoSection/ListFlowDetailSelector';
import ModelSelector from '~/components/ListFlow/ModelSelector';
import { useListFlowParcel } from '~/hooks/listFlow/useListFlowCategories';
import { useListFlowDetails } from '~/hooks/listFlow/useListFlowDetails';
import { useListFlowModels } from '~/hooks/listFlow/useListFlowModels';
import {
  changeModel,
  getListFlowDetails,
  getListFlowItemDetailIds,
  getListFlowModel,
} from '~/store/listFlow/slices/info';
import { changedDefaultParcel } from '~/store/listFlow/slices/shipping';
import { useAppDispatch, useAppSelector } from '~/store/store';
import { RailsCategory } from '~/typings/services/rails/item';

interface Props {
  category: RailsCategory;
}

const ListFlowDetailSelections: React.FC<Props> = ({ category }) => {
  const dispatch = useAppDispatch();
  const detailsState = useAppSelector(getListFlowDetails);
  const detailIds = useAppSelector(getListFlowItemDetailIds);
  const model = useAppSelector(getListFlowModel);

  const { data: details, isLoading } = useListFlowDetails(category.id, {
    // Quiet TS, this is how you stable sort by a boolean property
    // @ts-ignore
    select: data => data.sort((a, b) => b.required - a.required),
  });

  const brandDetail = details?.find(d => d.slug === 'brand');

  const selectedBrand = detailsState?.['brand']?.selected;
  const { data: models, isSuccess } = useListFlowModels({
    categoryId: category.id,
    brandId: selectedBrand === 'variations' ? undefined : selectedBrand?.id,
  });
  const { data: parcel } = useListFlowParcel(category?.id, detailIds, {
    keepPreviousData: true,
  });

  useEffect(() => {
    dispatch(changedDefaultParcel(parcel));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category?.id, parcel?.id]);

  if (isLoading) {
    return <Loader loading />;
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
        {brandDetail && (
          <ListFlowDetailSelector detail={brandDetail} searchable />
        )}

        {category.has_models && (
          <Field label="Model">
            <ModelSelector
              id="model-selector"
              categoryId={category?.id}
              clearable
              brandId={
                selectedBrand === 'variations' ? undefined : selectedBrand?.id
              }
              models={models}
              onChange={model => dispatch(changeModel(model))}
              placeholder={
                (!models || models.length == 0) && isSuccess
                  ? 'No models available'
                  : undefined
              }
              value={model}
            />
          </Field>
        )}
      </div>

      <div className="grid grid-cols-1 gap-x-4 md:grid-cols-3">
        {details
          ?.filter(d => d.slug !== 'brand' && d.slug !== 'category')
          ?.map(detail => (
            <ListFlowDetailSelector key={detail.slug} detail={detail} />
          ))}
      </div>
    </>
  );
};

export default ListFlowDetailSelections;
